import React, { useState } from 'react';
import Modal from './ui/Modal';
import Input from './ui/Input';
import Button from './ui/Button';
import { ICONS } from '../constants';
import { apiPost } from '../utils/api';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onSuccess: () => void;
}

export const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({
  isOpen,
  onSuccess,
}) => {
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!oldPassword || !newPassword || !confirmPassword) {
      setError('Пожалуйста, заполните все поля');
      return;
    }

    if (newPassword.length < 6) {
      setError('Новый пароль должен содержать минимум 6 символов');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (oldPassword === newPassword) {
      setError('Новый пароль должен отличаться от текущего');
      return;
    }

    setIsSubmitting(true);

    try {
      await apiPost('/auth/change-password-first-login', {
        oldPassword,
        newPassword,
      });

      // Success
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      onSuccess();
    } catch (err: any) {
      console.error('Error changing password:', err);
      setError(err?.response?.data?.error || 'Ошибка при смене пароля. Проверьте правильность текущего пароля.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}} // Cannot close - must change password
      title="🔒 Смена пароля обязательна"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="mb-6 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <ICONS.AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-200">
              <p className="font-semibold mb-1">Первый вход в систему</p>
              <p className="text-gray-300">
                Для обеспечения безопасности вашей учётной записи необходимо сменить временный пароль на постоянный.
                Эта модалка не закрывается до успешной смены пароля.
              </p>
            </div>
          </div>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        <Input
          label="Текущий (временный) пароль *"
          type={showOldPassword ? 'text' : 'password'}
          value={oldPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOldPassword(e.target.value)}
          placeholder="Введите временный пароль из письма"
          required
          disabled={isSubmitting}
        />
        <div className="flex items-center gap-2 -mt-2">
          <input
            type="checkbox"
            id="show-old-password"
            checked={showOldPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowOldPassword(e.target.checked)}
            className="rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
          />
          <label htmlFor="show-old-password" className="text-sm text-gray-400 cursor-pointer">
            Показать пароль
          </label>
        </div>

        <Input
          label="Новый пароль *"
          type={showNewPassword ? 'text' : 'password'}
          value={newPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
          placeholder="Минимум 6 символов"
          required
          disabled={isSubmitting}
        />
        <div className="flex items-center gap-2 -mt-2">
          <input
            type="checkbox"
            id="show-new-password"
            checked={showNewPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowNewPassword(e.target.checked)}
            className="rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
          />
          <label htmlFor="show-new-password" className="text-sm text-gray-400 cursor-pointer">
            Показать пароль
          </label>
        </div>

        <Input
          label="Подтвердите новый пароль *"
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmPassword(e.target.value)}
          placeholder="Повторите новый пароль"
          required
          disabled={isSubmitting}
        />
        <div className="flex items-center gap-2 -mt-2">
          <input
            type="checkbox"
            id="show-confirm-password"
            checked={showConfirmPassword}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setShowConfirmPassword(e.target.checked)}
            className="rounded border-gray-600 bg-gray-700 text-emerald-500 focus:ring-emerald-500"
          />
          <label htmlFor="show-confirm-password" className="text-sm text-gray-400 cursor-pointer">
            Показать пароль
          </label>
        </div>

        <div className="pt-4 border-t border-gray-700">
          <Button
            type="submit"
            className="w-full flex items-center justify-center gap-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <ICONS.Loader2 className="w-4 h-4 animate-spin" />
                Смена пароля...
              </>
            ) : (
              <>
                <ICONS.Check className="w-4 h-4" />
                Сменить пароль
              </>
            )}
          </Button>
        </div>

        <div className="text-xs text-gray-400 text-center">
          После успешной смены пароля вы сможете продолжить работу в системе
        </div>
      </form>
    </Modal>
  );
};

